import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AnalysisDateRangeOption, DATE_RANGE_FEATURE, TDateRangeState, VESSEL_LOCATION_OPTIONS } from "../types/dateRangeTypes";
import { RootState } from "../../../core/store";
import { DateValue, CalendarDate } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import { createYearMonthRange } from "../utils/dateRangeHelpers";
import { 
    trackExceptionWithContext
} from '../../../services/application-insights/appInsights';
import { 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../../services/application-insights/appInsightsConstants';

const initialState: TDateRangeState = {
    vesselLocationsDate: {
        selectedOption: 'latest',
        specificDate: null,
    },
    analysisDateRange: {
        selectedOption: null,
        specificYear: null,
        dateRange: null,
    },
};

const dateRangeSlice = createSlice({
    name: DATE_RANGE_FEATURE,
    initialState,
    reducers: {
        setDateRange: (state, action: PayloadAction<{ vesselLocationsDate?: TDateRangeState['vesselLocationsDate']; analysisDateRange?: TDateRangeState['analysisDateRange'] }
        >) => {
            try {
                if (action.payload.vesselLocationsDate) {
                    state.vesselLocationsDate = action.payload.vesselLocationsDate;
                }
                if (action.payload.analysisDateRange) {
                    state.analysisDateRange = {
                        selectedOption: action.payload.analysisDateRange.selectedOption,
                        specificYear: action.payload.analysisDateRange.specificYear,
                        dateRange: action.payload.analysisDateRange.dateRange,
                        yearMonthRange: createYearMonthRange(action.payload.analysisDateRange.dateRange)
                    }
                }
            } catch (error) {
                // Only log exceptions for Redux state update failures
                trackExceptionWithContext(
                    error instanceof Error ? error : new Error('Date range state update failed'),
                    {
                        component: AppInsightsComponents.DATE_RANGE_SLICE,
                        additionalData: {
                            errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
                            action: 'setDateRange',
                            hasVesselLocationsData: !!action.payload.vesselLocationsDate,
                            hasAnalysisDateRange: !!action.payload.analysisDateRange,
                            vesselLocationOption: action.payload.vesselLocationsDate?.selectedOption,
                            analysisOption: action.payload.analysisDateRange?.selectedOption
                        }
                    }
                );
            }
        },
        initializeDateRangeFromMainMap: (state, action: PayloadAction<{
            selectedOption: AnalysisDateRangeOption,
            specificYear: number | null,
            dateRange: RangeValue<DateValue> | null,
            specificDate: CalendarDate | null
        }>) => {
            try {
                // This action can be dispatched from a component after reading the main map date range
                state.analysisDateRange = {
                    selectedOption: action.payload.selectedOption,
                    specificYear: action.payload.specificYear,
                    dateRange: action.payload.dateRange,
                    yearMonthRange: createYearMonthRange(action.payload.dateRange)
                };
                state.vesselLocationsDate.selectedOption = VESSEL_LOCATION_OPTIONS.SPECIFIC;
                state.vesselLocationsDate.specificDate = action.payload.specificDate ? action.payload.specificDate : null;
            } catch (error) {
                // Only log exceptions for Redux initialization failures
                trackExceptionWithContext(
                    error instanceof Error ? error : new Error('Date range initialization from main map failed'),
                    {
                        component: AppInsightsComponents.DATE_RANGE_SLICE,
                        additionalData: {
                            errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
                            action: 'initializeDateRangeFromMainMap',
                            selectedOption: action.payload.selectedOption,
                            hasSpecificYear: !!action.payload.specificYear,
                            hasDateRange: !!action.payload.dateRange,
                            hasSpecificDate: !!action.payload.specificDate
                        }
                    }
                );
            }
        },
    },
});

export const {
    setDateRange,
    initializeDateRangeFromMainMap
} = dateRangeSlice.actions;

export default dateRangeSlice.reducer;

// Selectors
export const selectFeatureDateRangeVesselLocationsDate = (state: RootState) => state.featureDateRangeSlice.vesselLocationsDate;
export const selectFeatureDateRangeAnalysisDateRange = (state: RootState) => state.featureDateRangeSlice.analysisDateRange;
export const selectFeatureDateRangeYearMonthRange = (state: RootState) => state.featureDateRangeSlice.analysisDateRange.yearMonthRange;
